import { strict as assert } from 'assert';

import type { VersionedResource } from '../../types';

export enum QueryKey {
    ID = 'id',
    KEY = 'key',
    CODE = 'code',
}

export function assertIsPresent<T>(actual: T | null | undefined, message?: string | Error): asserts actual is T {
    assert.notEqual(actual, null, message);
    assert.notEqual(actual, undefined, message);
}

export function assertPresence<T>(input: T | null | undefined, message = 'Value is required'): T {
    assertIsPresent(input, message);
    return input;
}

export function queryFromIdAndVersion(resource: VersionedResource): string {
    return `id = "${resource.id}" and version = "${resource.version}"`;
}

export function queryResourcesFromIdAndVersion(resources: VersionedResource[]): string {
    return resources.map(resource => `id = "${resource.id}" and version = "${resource.version}"`).join(' or ');
}

export function commaSeparatedValues(values: string[] | undefined) {
    return values?.map(value => `"${value}"`).join(', ');
}

export function getQueryPredicate(queryList: string[], key: QueryKey): string {
    const values = commaSeparatedValues(queryList);
    return values ? `${key} in (${values})` : '';
}

type QueryArgs = {
    queryArgs?: {
        sort?: string | string[];
        where?: string | string[];
        expand?: string | string[];
        withTotal?: boolean;
    };
};

function mergeArgValues(value: string | string[] = [], additional: string | string[] = []): string[] {
    const values = Array.isArray(value) ? value : [value];
    const additionalValues = Array.isArray(additional) ? additional : [additional];

    return [...values, ...additionalValues].filter(val => val);
}

export function extendQueryArgs<T extends QueryArgs>(sourceArgs?: QueryArgs, additionalArgs?: QueryArgs): T {
    return <T>{
        ...sourceArgs,
        queryArgs: {
            ...sourceArgs?.queryArgs,
            where: mergeArgValues(sourceArgs?.queryArgs?.where, additionalArgs?.queryArgs?.where),
            expand: mergeArgValues(sourceArgs?.queryArgs?.expand, additionalArgs?.queryArgs?.expand),
            sort: mergeArgValues(sourceArgs?.queryArgs?.sort, additionalArgs?.queryArgs?.sort),
        },
    };
}
